import api from "utils/api";
import { Row, Col } from "react-bootstrap";
import DialogueCard from "./DialogueCard";
import { useHistory } from "react-router-dom";
import SkipToDashboardLink from "./SkipToDashboardLink";
import { snakeCaseToSentenceCase } from "utils/formatText";
import { useMutation } from "@tanstack/react-query";
import { completeAmzAdvConnect, completeAmzProfileSelection } from "redux/reducers/amzOnboardReducer";
import { setCurrentUser } from "redux/reducers/userReducer";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { SearchInput } from "components/fields/base/TextField";
import { useMemo, useState } from "react";
import { css } from "../../../../styled-system/css";

const Profiles = ({ profiles }) => {
  const [search, setSearch] = useState("");
  const history = useHistory();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);

  const savePrimaryProfile = useMutation({
    mutationFn: async (primaryProfileData) => {
      return await api.post("/api/accounts/", primaryProfileData);
    },
    onSuccess: async (res, variables) => {
      // Trigger secondary creations once profile has been assigned to primary org
      history.replace({
        search: null,
      });

      dispatch(completeAmzProfileSelection());
      dispatch(completeAmzAdvConnect());

      // Keep redux state up to date with fields updated in backend for initial visit to the dashboard and providing right redirects for SP-API auth based on org type
      dispatch(
        setCurrentUser({
          current_profile: variables.selected_profile,
          organization_type: variables.type,
        }),
      );

      history.push("/onboarding/v2/connect");
    },
    onError: async (error, variables) => {
      // If user loses tokens, send back to sign in page
      if (error.response.status === 401) {
        history.push("/");
      }

      // If error assigning primary profile which can be caused by race condition, try switching user to already existing profile
      // to prevent them from getting stuck on the profile selection screen
      if (error.response.status === 500) {
        const destinationAccountData = {
          account: variables.selected_profile,
        };

        if (variables.type === "vendor") {
          destinationAccountData["vendor"] = true;
        }
        const res = await api.put("/api/accounts/", destinationAccountData);

        if (res.status === 204) {
          dispatch(
            setCurrentUser({
              current_profile: variables.selected_profile,
              organization_type: variables.type,
            }),
          );
          history.replace({
            search: null,
            pathname: "/onboarding/v2/connect",
          });
        }
      }
    },
  });

  const filteredProfiles = useMemo(() => {
    if (search) {
      return profiles?.filter((profile) => profile.label.toLowerCase()?.includes(search?.toLowerCase()));
    }

    return profiles;
  }, [profiles, search]);

  if (user.current_profile) {
    history.push("/onboarding/v2/connect");
  }
  return (
    <>
      <Row className="my-5 w-100" style={{ maxHeight: "72vh", overflowY: "auto" }}>
        <Col xs={12} lg={{ offset: 2, span: 8 }}>
          <h2 className="mt-5 mb-1 fw-600">Amazon Advertising API</h2>
          <p className="fs-4">We require access to both the SP-API and Amazon Advertising API</p>
          <h4 className="fw-500">Select Your Default Profile</h4>
          <SearchInput value={search} onChange={setSearch} placeholder={"Search for a profile..."} />
        </Col>
        <Col xs={12} lg={{ offset: 2, span: 8 }}>
          {filteredProfiles.map((profile) => (
            <DialogueCard
              key={profile.value}
              contents={{
                value: profile.value,
                title: profile.label,
                description: snakeCaseToSentenceCase(profile.type),
              }}
              justify="center"
              additionalClasses="my-5"
              onClick={() => {
                const primaryProfileData = {
                  selected_profile: profile.value,
                  type: profile.type,
                };

                savePrimaryProfile.mutate(primaryProfileData);
              }}
            />
          ))}
        </Col>
        <Col xs={12} lg={{ offset: 2, span: 8 }}>
          <p className={css({ fontSize: "medium", textAlign: "center" })}>
            Not seeing a profile you have access to? Please contact Customer Success as it may have already been
            connected to Trellis.
          </p>
        </Col>
      </Row>
      <SkipToDashboardLink />
    </>
  );
};

export default Profiles;
